import { Entity, Pile, Random, Resources } from "~/utils";
import { TechTile } from "./TechTile";
import { Card } from "./card/Card";
import { cardsDefRepository } from "./card/cardsDefRepository";
import {
  Dreadnought,
  FactionAffinity,
  FactionType,
  Meaple,
  PlayerLeader,
  ResourceType,
  Troop,
  UnitType,
} from "./constant";
import { AgentEffectDescription, AgentEffectDescriptionRaw } from "./effect";
import { IntrigueCard } from "./intrigueCards/IntrigueCardProps";

export type NewPlayerProps = {
  name: string;
  id: string;
  playerLeader: PlayerLeader;
  garnisonCount?: number;
  dreadnoughtCount: number;
  cardNames?: string[];
  startingResources?: Record<ResourceType, number>;
};

export type PlayerProps = Entity & {
  name: string;
  deck: Pile<Card>;
  discard: Pile<Card>;
  hand: Pile<Card>;
  playZone: Pile<Card>;
  revealZone: Pile<Card>;
  trashZone: Pile<Card>;
  resources: Resources;
  bonusInfluencePoints: number;
  victoryPoints: number;
  bonusCombatPoints: number;
  orbitTroops: Pile<Troop>;
  garnisonTroops: Pile<Troop>;
  combatTroops: Pile<Troop>;
  orbitDreadnought: Pile<Dreadnought>;
  garnisonDreadnought: Pile<Dreadnought>;
  combatDreadnought: Pile<Dreadnought>;
  techNegociators: Pile<Troop>;
  hasSiege: boolean;
  hasExtraMeaple: boolean;
  hasRevealed: boolean;
  workerPile: Pile<Meaple>;
  factionAffinities: FactionAffinity[];
  intrigueCards: Pile<IntrigueCard>;
  startOfTurnGarnison: { troop: number; dreadnought: number };
  startOfTurnConflict: { troop: number; dreadnought: number };
  ignoreAgentIcons: boolean;
  ignoreOccupiedAgentSlots: boolean;
  passedIntrigue: boolean;
  reservedShopCards: Pile<Entity & { card: Card; discount: number }>;
  playerLeader: PlayerLeader;
  TSMFDiscount: number;
  canBuyOnDeck: boolean;
  hasPlayedGlobalEffect: boolean;
  freighterLevel: number;
  techTiles: Pile<TechTile>;
  onWin?: AgentEffectDescriptionRaw;
  checkpointIntrigueCardCount: number;
};

export class Player implements PlayerProps {
  id: string;
  name: string;
  deck: Pile<Card>;
  discard: Pile<Card>;
  hand: Pile<Card>;
  playZone: Pile<Card>;
  revealZone: Pile<Card>;
  trashZone: Pile<Card>;
  resources: Resources;
  bonusInfluencePoints: number;
  victoryPoints: number;
  bonusCombatPoints: number;
  orbitTroops: Pile<Troop>;
  garnisonTroops: Pile<Troop>;
  combatTroops: Pile<Troop>;
  orbitDreadnought: Pile<Dreadnought>;
  garnisonDreadnought: Pile<Dreadnought>;
  combatDreadnought: Pile<Dreadnought>;
  techNegociators: Pile<Troop>;
  hasSiege: boolean;
  hasExtraMeaple: boolean;
  hasRevealed: boolean;
  workerPile: Pile<Meaple>;
  factionAffinities: FactionAffinity[];
  intrigueCards: Pile<IntrigueCard>;
  startOfTurnGarnison: { troop: number; dreadnought: number };
  startOfTurnConflict: { troop: number; dreadnought: number };
  ignoreAgentIcons: boolean;
  ignoreOccupiedAgentSlots: boolean;
  passedIntrigue: boolean;
  reservedShopCards: Pile<Entity & { card: Card; discount: number }>;
  playerLeader: PlayerLeader;
  TSMFDiscount: number;
  canBuyOnDeck: boolean;
  hasPlayedGlobalEffect: boolean;
  freighterLevel: number;
  techTiles: Pile<TechTile>;
  onWin?: AgentEffectDescriptionRaw;
  checkpointIntrigueCardCount: number;

  constructor(props: PlayerProps) {
    Object.assign(this, props);
  }
  get staticVictoryPoints(): number {
    const { factionAffinities } = this;
    const factionPoints = factionAffinities.reduce(
      (acc, { level, hasPoint }) =>
        acc + (hasPoint ? 1 : 0) + (level >= 2 ? 1 : 0),
      0
    );
    return factionPoints;
  }

  get totalVictoryPoints(): number {
    return this.staticVictoryPoints + this.victoryPoints;
  }
  getFactionAffinity(faction: FactionType): FactionAffinity {
    const affinityOpt = this.factionAffinities.find(
      (_) => _.faction === faction
    );
    if (!affinityOpt) {
      throw new Error(`could not find affinity for faction ${faction}`);
    }
    return affinityOpt;
  }
  draw(count: number) {
    if (this.deck.canTake(count)) {
      this.deck.moveCount(this.hand, count);
    } else {
      const cardsInDeck = this.deck.length;
      const cardsLeft = count - cardsInDeck;
      this.deck.moveCount(this.hand, cardsInDeck);
      this.shuffleDiscard();
      this.deck.moveCount(this.hand, Math.min(cardsLeft, this.deck.length));
    }
  }
  lookAtTop(): Card {
    if (this.deck.isEmpty()) {
      this.shuffleDiscard();
    }
    const lastCard = this.deck.last();
    if (!lastCard) {
      throw new Error("no more cards");
    }
    return lastCard;
  }
  private shuffleDiscard() {
    this.deck.pile = Random.shuffle(this.discard.pile);
    this.discard = Pile.empty();
  }

  get combatPoints(): number {
    const {
      bonusCombatPoints,
      combatTroops,
      combatDreadnought,
      playerLeader: { globalEffect },
    } = this;
    if (!combatTroops.length && !combatDreadnought) return 0;
    const dreadnoughtStrength = AgentEffectDescription.is(
      globalEffect,
      "dreadnoughtStrength"
    )
      ? 4
      : 3;
    return (
      bonusCombatPoints +
      combatTroops.length * 2 +
      combatDreadnought.length * dreadnoughtStrength
    );
  }
  get ownedCards(): Card[] {
    const { hand, deck, discard, playZone, revealZone } = this;
    return [hand, deck, discard, playZone, revealZone].flatMap(
      (zone) => zone.pile
    );
  }
  get cards(): Card[] {
    const { ownedCards, trashZone, reservedShopCards } = this;
    return [
      ...ownedCards,
      ...trashZone.pile,
      ...reservedShopCards.pile.map(({ card }) => card),
    ];
  }

  get influencePoints(): number {
    return this.bonusInfluencePoints + (this.hasSiege ? 2 : 0);
  }

  static new(props: NewPlayerProps): Player {
    const {
      cardNames = [
        "Diplomacy",
        "Convincing argument",
        "Convincing argument",
        "Dagger",
        "Dagger",
        "Seek allies",
        "Signet ring",
        "Reconnaissance",
        "Dune, the desert planet",
        "Dune, the desert planet",
      ],
      garnisonCount = 3,
      startingResources = {
        [ResourceType.Water]: 1,
        [ResourceType.Solaris]: 0,
        [ResourceType.Spice]: 0,
      },
    } = props;
    const totalTroop = 12;
    const playerId = props.id;
    const player = new Player({
      id: playerId,
      name: props.name,
      playerLeader: props.playerLeader,
      discard: Pile.empty<Card>(),
      hand: Pile.empty<Card>(),
      deck: Pile.fromArray(
        Random.shuffle(
          cardNames.map((cardName) => cardsDefRepository.fromName(cardName))
        )
      ),
      playZone: Pile.empty<Card>(),
      revealZone: Pile.empty<Card>(),
      trashZone: Pile.empty<Card>(),
      resources: Resources.fromRecord(startingResources),
      bonusInfluencePoints: 0,
      victoryPoints: 0,
      bonusCombatPoints: 0,
      orbitTroops: Pile.fromArray(
        [...new Array(totalTroop)].map((_, index) => ({
          owner: playerId,
          id: `${playerId}-troop-${index}`,
        }))
      ),
      garnisonTroops: Pile.empty(),
      combatTroops: Pile.empty(),
      orbitDreadnought: Pile.fromArray(
        [...new Array(props.dreadnoughtCount)].map((_, index) => ({
          owner: playerId,
          id: `${playerId}-dreadnought-${index}`,
        }))
      ),
      garnisonDreadnought: Pile.empty(),
      combatDreadnought: Pile.empty(),
      techNegociators: Pile.empty(),
      hasSiege: false,
      hasExtraMeaple: false,
      hasRevealed: false,
      ignoreAgentIcons: false,
      ignoreOccupiedAgentSlots: false,
      workerPile: Pile.fromArray(
        [...new Array(2)].map((_, index) =>
          Meaple.new(`${playerId}-${index}`, playerId)
        )
      ),
      factionAffinities: Object.values(FactionType).map((faction) => ({
        faction,
        hasPoint: false,
        level: 0,
      })),
      intrigueCards: Pile.empty(),
      startOfTurnGarnison: {
        troop: 0,
        dreadnought: 0,
      },
      startOfTurnConflict: {
        troop: 0,
        dreadnought: 0,
      },
      passedIntrigue: false,
      reservedShopCards: Pile.empty(),
      TSMFDiscount: 0,
      canBuyOnDeck: false,
      hasPlayedGlobalEffect: false,
      freighterLevel: 0,
      techTiles: Pile.empty(),
      checkpointIntrigueCardCount: 0,
    });
    player.moveToGarnison(garnisonCount);
    return player;
  }
  getGarnison(type: UnitType) {
    const { garnisonDreadnought, garnisonTroops } = this;
    return type === "troop" ? garnisonTroops : garnisonDreadnought;
  }
  getCombatPile(type: UnitType) {
    const { combatDreadnought, combatTroops } = this;
    return type === "troop" ? combatTroops : combatDreadnought;
  }
  moveToGarnison(amount: number) {
    const { orbitTroops, garnisonTroops } = this;
    orbitTroops.moveCount(garnisonTroops, amount);
  }
  moveToCombat(amount: number) {
    const { garnisonTroops } = this;
    garnisonTroops.moveCount(garnisonTroops, amount);
  }
  getCard(cardId: string) {
    const cardOpt = this.cards.find((card) => card.id === cardId);
    if (!cardOpt) {
      throw new Error(`could not find card with id=${cardId}`);
    }
    return cardOpt;
  }
  getResource(resourceType: ResourceType): number {
    return this.resources.getResource(resourceType)?.value ?? 0;
  }
  get TSMFCardCount(): number {
    return this.ownedCards.filter(
      (card) => cardsDefRepository.getDef(card).name === "The spice must flow"
    ).length;
  }
}
